import React from "react"
import Dropdown from "react-bootstrap/Dropdown"
import DropdownButton from "react-bootstrap/DropdownButton"
import {
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    ChevronsLeft,
    ChevronsRight,
} from "lucide-react"
import V2TableRefetchLoader from "../common/V2TableRefetchLoader.jsx"
import TableHeaderMenu from "./TableHeaderMenu.jsx"

const DEFAULT_COLUMN_MIN_WIDTH = 60
const OVERFLOW_TOLERANCE_PX = 2
const TOP_SCROLLBAR_HEIGHT_PX = 18
const FIT_ROWS_BOTTOM_GAP_PX = 8
// Debug-only: force horizontal scroll UI even without overflow.
const DEBUG_FORCE_HORIZONTAL_SCROLLBAR = false
/** Space between refetch loader bottom edge and table pagination footer top (px). */
const REFETCH_LOADER_ABOVE_FOOTER_GAP_PX = 12
/** Table borders / subpixels: initial loading min-height must not exceed the flex slot. */
const LOADING_BODY_HEIGHT_FUDGE_PX = 4
const RESIZE_SETTLE_DELAY_MS = 2000 // Set high because this was able to catch some race conditions
const RESIZE_LIVE_UPDATE_INTERVAL_MS = 160

function getBreakpointKey(viewportWidth) {
    if (viewportWidth >= 1400) {
        return "xxl"
    }
    if (viewportWidth >= 1200) {
        return "xl"
    }
    if (viewportWidth >= 992) {
        return "lg"
    }
    if (viewportWidth >= 768) {
        return "md"
    }
    if (viewportWidth >= 576) {
        return "sm"
    }
    return "base"
}

function getResponsiveValue(value, breakpointKey) {
    if (value == null) {
        return null
    }
    if (typeof value === "number" || typeof value === "string") {
        return value
    }

    const order = ["base", "sm", "md", "lg", "xl", "xxl"]
    const targetIndex = order.indexOf(breakpointKey)
    if (targetIndex === -1) {
        return value.base ?? null
    }

    for (let i = targetIndex; i >= 0; i -= 1) {
        const candidate = value[order[i]]
        if (candidate != null) {
            return candidate
        }
    }
    return null
}

function toPixels(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
        return value
    }
    if (typeof value !== "string") {
        return null
    }

    const trimmed = value.trim()
    if (!trimmed) {
        return null
    }
    if (trimmed.endsWith("px")) {
        const parsed = Number(trimmed.slice(0, -2))
        return Number.isFinite(parsed) ? parsed : null
    }
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
}

function allocatePreferredWidths(minWidths, preferredWidths, tableWidth) {
    const result = [...minWidths]
    let remaining = tableWidth - minWidths.reduce((sum, width) => sum + width, 0)

    if (remaining <= 0) {
        return result
    }

    // Step 1: Grow equally until every column reaches preferred width.
    let activeIndices = result
        .map((width, index) => ({ width, index }))
        .filter(({ width, index }) => width < preferredWidths[index])
        .map(({ index }) => index)

    while (remaining > 0.0001 && activeIndices.length > 0) {
        const equalStep = remaining / activeIndices.length
        let consumed = 0
        const nextActive = []

        for (const index of activeIndices) {
            const toPreferred = preferredWidths[index] - result[index]
            const add = Math.min(equalStep, toPreferred)
            result[index] += add
            consumed += add
            if (result[index] + 0.0001 < preferredWidths[index]) {
                nextActive.push(index)
            }
        }

        remaining -= consumed
        activeIndices = nextActive
    }

    if (remaining <= 0.0001) {
        return result
    }

    // Step 2: Distribute remaining proportionally to preferred widths.
    const preferredSum = preferredWidths.reduce((sum, width) => sum + width, 0)
    if (preferredSum <= 0) {
        return result
    }

    for (let i = 0; i < result.length; i += 1) {
        result[i] += remaining * (preferredWidths[i] / preferredSum)
    }

    return result
}

function haveSameWidths(prev, next) {
    if (prev.length !== next.length) {
        return false
    }
    for (let i = 0; i < prev.length; i += 1) {
        if (Math.abs(prev[i] - next[i]) > 0.5) {
            return false
        }
    }
    return true
}

function normalizeWidthsToTable(widths, tableWidth) {
    if (widths.length === 0) {
        return []
    }

    const floored = widths.map((width) => Math.floor(width))
    const floorSum = floored.reduce((sum, width) => sum + width, 0)
    const target = Math.max(0, Math.round(tableWidth))
    let remaining = target - floorSum

    if (remaining <= 0) {
        return floored
    }

    const byFraction = widths
        .map((width, index) => ({ index, frac: width - Math.floor(width) }))
        .sort((a, b) => b.frac - a.frac)

    let i = 0
    while (remaining > 0) {
        floored[byFraction[i % byFraction.length].index] += 1
        remaining -= 1
        i += 1
    }

    return floored
}

/**
 * @param {{
 *   headers: any[],
 *   rows: any[],
 *   renderRow: (
 *     row: any,
 *     index: number,
 *     tools: { getCellProps: Function, getRowProps: Function }
 *   ) => React.ReactNode,
 *   isLoadingInitial?: boolean,
 *   totalRows?: number,
 *   page?: number,
 *   perPage?: number,
 *   onPageChange?: (nextPage: number) => void,
 *   onPerPageChange?: (nextPerPage: number | "fit") => void,
 *   showOnMobile?: boolean,
 *   perPageSelection?: number | "fit",
 *   onFitPerPageChange?: ((rows: number) => void) | undefined,
 *   stretchFooterToBottom?: boolean,
 *   showPagination?: boolean,
 *   rowsPerPageOptions?: number[],
 *   showFitOption?: boolean,
 *   selectedRowKey?: string | number | null,
 *   getRowKey?: ((row: any, index: number) => string | number) | undefined,
 *   onRowSelect?: ((row: any, index: number) => void) | undefined,
 *   showRefetchLoader?: boolean,
 * }} props
 */
export default function TableWithPagination({
    headers,
    rows,
    renderRow,
    isLoadingInitial = false,
    totalRows = 0,
    page = 1,
    perPage = 25,
    onPageChange,
    onPerPageChange,
    showOnMobile = false,
    perPageSelection = undefined,
    onFitPerPageChange = undefined,
    stretchFooterToBottom = false,
    showPagination = true,
    rowsPerPageOptions = [25, 50, 75, 100],
    showFitOption = true,
    selectedRowKey = null,
    getRowKey = undefined,
    onRowSelect = undefined,
    showRefetchLoader = false,
}) {
    const rootRef = React.useRef(/** @type {HTMLDivElement | null} */(null))
    const topScrollRef = React.useRef(/** @type {HTMLDivElement | null} */(null))
    const topScrollInnerRef = React.useRef(/** @type {HTMLDivElement | null} */(null))
    const bottomScrollRef = React.useRef(/** @type {HTMLDivElement | null} */(null))
    const footerRef = React.useRef(/** @type {HTMLDivElement | null} */(null))
    const footerFillerRef = React.useRef(/** @type {HTMLDivElement | null} */(null))
    const syncLockRef = React.useRef(false)
    const fitNeedsRecalcRef = React.useRef(false)
    const prevPerPageSelectionRef = React.useRef(
        /** @type {number | "fit" | undefined} */(undefined)
    )
    const prevIsLoadingInitialRef = React.useRef(isLoadingInitial)
    const [hasHorizontalOverflow, setHasHorizontalOverflow] = React.useState(false)
    const [columnWidths, setColumnWidths] = React.useState(
        /** @type {number[]} */([])
    )
    const [tableWidthPx, setTableWidthPx] = React.useState(0)
    const [loadingBodyMinHeightPx, setLoadingBodyMinHeightPx] = React.useState(320)
    const [footerBlockHeightPx, setFooterBlockHeightPx] = React.useState(0)
    const [footerFillerBlockHeightPx, setFooterFillerBlockHeightPx] = React.useState(0)
    const [openHeaderMenuKey, setOpenHeaderMenuKey] = React.useState(/** @type {string | null} */(null))

    const totalPages = Math.max(1, Math.ceil(totalRows / perPage))
    const rangeStart = totalRows > 0 ? (page - 1) * perPage + 1 : 0
    const rangeEnd = totalRows > 0 ? Math.min(page * perPage, totalRows) : 0
    const canGoPrev = page > 1
    const canGoNext = page < totalPages
    const activePerPageSelection = /** @type {number | "fit"} */ (
        perPageSelection ?? perPage
    )
    const displayClass = (
        showOnMobile ?
            (stretchFooterToBottom ? "d-flex" : "d-block") :
            (stretchFooterToBottom ? "d-none d-lg-flex" : "d-none d-lg-block")
    )
    const headersByKey = Object.fromEntries(headers.map((header) => [header.key, header]))

    function getCellProps(columnKey, style = {}, className = "") {
        return {
            className,
            style: {
                ...style,
            },
        }
    }

    function getRowProps(row, index, style = {}, className = "") {
        const rowKey = getRowKey ? getRowKey(row, index) : index
        const isSelected = selectedRowKey != null && rowKey === selectedRowKey
        const hasSelectionHandler = typeof onRowSelect === "function"
        return {
            className,
            style: {
                ...(isSelected ? { backgroundColor: "rgba(15, 97, 112, 0.08)" } : {}),
                ...(hasSelectionHandler ? { cursor: "pointer" } : {}),
                ...style,
            },
            onClick: hasSelectionHandler ? () => onRowSelect(row, index) : undefined,
        }
    }

    function renderHeaderLabel(label) {
        if (typeof label === "string" || typeof label === "number") {
            return (
                <span
                    title={String(label)}
                    style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {label}
                </span>
            )
        }
        return label
    }

    function renderHeaderContent(header) {
        const sortKey = header.sortKey ?? header.key
        const label = renderHeaderLabel(header.label)
        const headerMenuEnabled = header.headerMenu !== false && header.key !== "select"
        const sortable = Boolean(header.sortable && typeof header.onSort === "function")
        const filterable = Boolean(header.filterable && typeof header.onFilter === "function")
        const hasMenu = headerMenuEnabled && (sortable || filterable)

        if (!hasMenu) {
            return (
                <div
                    style={{
                        width: "100%",
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "4px",
                        lineHeight: 1.2,
                        overflow: "hidden",
                    }}
                >
                    <span className="text-truncate" style={{ minWidth: 0, width: "100%" }}>
                        {label}
                    </span>
                </div>
            )
        }

        return (
            <TableHeaderMenu
                label={label}
                sortKey={sortKey}
                sortBy={header.sortBy}
                sortDirection={header.sortDirection}
                sortable={sortable}
                filterable={filterable}
                isFilterActive={Boolean(header.isFilterActive)}
                sortAscLabel={header.sortAscLabel}
                sortDescLabel={header.sortDescLabel}
                isOpen={openHeaderMenuKey === header.key}
                onOpenChange={(nextOpen) => {
                    setOpenHeaderMenuKey(nextOpen ? header.key : null)
                }}
                onSort={header.onSort}
                onFilter={header.onFilter}
            />
        )
    }

    React.useLayoutEffect(() => {
        if (!showPagination) {
            setFooterBlockHeightPx(0)
            return
        }
        const footerEl = footerRef.current
        if (!footerEl) {
            setFooterBlockHeightPx(0)
            return
        }
        function measure() {
            const el = footerRef.current
            if (!el) {
                return
            }
            setFooterBlockHeightPx(Math.ceil(el.getBoundingClientRect().height))
        }
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(footerEl)
        return () => ro.disconnect()
    }, [showPagination, totalRows, page, perPage, rows.length, isLoadingInitial])

    /**
     * When `stretchFooterToBottom` is on, a flex filler sits *below* the pagination bar.
     * `V2TableRefetchLoader` is absolutely positioned from the root bottom; without adding
     * this height, the chip is placed at the bottom of the filler (often off-screen).
     */
    React.useLayoutEffect(() => {
        const showFiller = showPagination && stretchFooterToBottom && !isLoadingInitial
        if (!showFiller) {
            setFooterFillerBlockHeightPx(0)
            return
        }
        const fillerEl = footerFillerRef.current
        if (!fillerEl) {
            setFooterFillerBlockHeightPx(0)
            return
        }
        function measure() {
            const el = footerFillerRef.current
            if (!el) {
                return
            }
            setFooterFillerBlockHeightPx(Math.ceil(el.getBoundingClientRect().height))
        }
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(fillerEl)
        return () => ro.disconnect()
    }, [showPagination, stretchFooterToBottom, isLoadingInitial, totalRows, page, perPage, rows.length])

    React.useEffect(() => {
        const wasFit = prevPerPageSelectionRef.current === "fit"
        const isFit = activePerPageSelection === "fit"
        if (isFit && !wasFit) {
            fitNeedsRecalcRef.current = true
        }
        prevPerPageSelectionRef.current = activePerPageSelection
    }, [activePerPageSelection])

    /** While first-load spinner shows, stretch the table body to fill the column; footer stays one row. */
    const stretchInitialLoadLayout = (
        Boolean(isLoadingInitial && stretchFooterToBottom && showPagination)
    )

    React.useEffect(() => {
        if (prevIsLoadingInitialRef.current && !isLoadingInitial && activePerPageSelection === "fit") {
            fitNeedsRecalcRef.current = true
        }
        prevIsLoadingInitialRef.current = isLoadingInitial
    }, [isLoadingInitial, activePerPageSelection])

    React.useEffect(() => {
        if (!topScrollRef.current || !topScrollInnerRef.current || !bottomScrollRef.current) {
            return
        }

        const topEl = topScrollRef.current
        const topInnerEl = topScrollInnerRef.current
        const bottomEl = bottomScrollRef.current
        const rootEl = rootRef.current
        const footerEl = footerRef.current

        function syncColumnWidths() {
            const columnCount = headers.length
            if (columnCount === 0) {
                setColumnWidths([])
                const width = Math.round(bottomEl.clientWidth)
                setTableWidthPx(width)
                return width
            }

            const breakpointKey = getBreakpointKey(window.innerWidth)
            const minWidths = headers.map((header) => {
                const rawMin = getResponsiveValue(header.minWidth, breakpointKey)
                const minPx = toPixels(rawMin)
                if (minPx == null) {
                    return DEFAULT_COLUMN_MIN_WIDTH
                }
                return Math.max(0, minPx)
            })

            const minTotal = minWidths.reduce((sum, width) => sum + width, 0)
            const tableWidth = Math.round(Math.max(minTotal, bottomEl.clientWidth))
            const defaultPreferredWidth = tableWidth / columnCount

            const preferredWidths = headers.map((header, index) => {
                const rawPreferred = getResponsiveValue(
                    header.preferredWidth ?? header.width,
                    breakpointKey
                )
                const preferredPx = toPixels(rawPreferred) ?? defaultPreferredWidth
                return Math.max(preferredPx, minWidths[index])
            })

            const nextWidthsRaw = allocatePreferredWidths(
                minWidths,
                preferredWidths,
                tableWidth
            )
            const nextWidths = normalizeWidthsToTable(nextWidthsRaw, tableWidth)

            setTableWidthPx(tableWidth)
            setColumnWidths((prev) => haveSameWidths(prev, nextWidths) ? prev : nextWidths)
            return tableWidth
        }

        function syncScrollMetrics() {
            const computedTableWidth = syncColumnWidths()
            const fallbackWidth = Math.max(bottomEl.scrollWidth, bottomEl.clientWidth)
            const contentWidth = computedTableWidth ?? fallbackWidth
            const overflow = contentWidth > bottomEl.clientWidth + OVERFLOW_TOLERANCE_PX

            topInnerEl.style.width = `${contentWidth}px`
            setHasHorizontalOverflow(overflow)

            if (!overflow) {
                topEl.scrollLeft = 0
                bottomEl.scrollLeft = 0
            } else {
                topEl.scrollLeft = bottomEl.scrollLeft
            }

            const tableEl = bottomEl.querySelector("table")
            if (tableEl) {
                const theadEl = tableEl.querySelector("thead")
                const headHeight = theadEl?.getBoundingClientRect().height ?? 0
                const raw = bottomEl.clientHeight - headHeight
                const available = Math.max(
                    0,
                    Math.floor(raw) - LOADING_BODY_HEIGHT_FUDGE_PX
                )
                setLoadingBodyMinHeightPx((prev) => (prev === available ? prev : available))
            }

            const fitCallback = /** @type {((rows: number) => void) | undefined} */ (
                onFitPerPageChange
            )
            const shouldRunFitCalculation = (
                fitNeedsRecalcRef.current &&
                !isLoadingInitial &&
                activePerPageSelection === "fit" &&
                typeof fitCallback === "function" &&
                rootEl &&
                footerEl
            )
            if (shouldRunFitCalculation) {
                const firstRowEl = tableEl?.querySelector("tbody tr")
                const theadEl = tableEl?.querySelector("thead")
                const tbodyEl = tableEl?.querySelector("tbody")
                let didComputeFit = false

                if (tableEl && firstRowEl && theadEl && tbodyEl) {
                    const rowHeight = firstRowEl.getBoundingClientRect().height
                    const renderedRows = tbodyEl.querySelectorAll("tr").length
                    const fillerHeight = (
                        footerFillerRef.current?.getBoundingClientRect().height ?? 0
                    )
                    const pageOverflowPx = Math.max(
                        0,
                        document.documentElement.scrollHeight - window.innerHeight
                    )
                    const growRows = Math.floor(fillerHeight / rowHeight)
                    const shrinkRows = Math.ceil(pageOverflowPx / rowHeight)

                    let fitRows = renderedRows + growRows - shrinkRows

                    // Fallback when filler/overflow deltas are unavailable or unstable.
                    if (!Number.isFinite(fitRows) || fitRows <= 0) {
                        const headerHeight = theadEl.getBoundingClientRect().height
                        const footerHeight = footerEl.getBoundingClientRect().height
                        const rootTop = rootEl.getBoundingClientRect().top
                        const availableHeight = (
                            window.innerHeight -
                            rootTop -
                            FIT_ROWS_BOTTOM_GAP_PX
                        )
                        const nonBodyHeight = (
                            headerHeight +
                            footerHeight +
                            (overflow ? TOP_SCROLLBAR_HEIGHT_PX : 0)
                        )
                        const rowsAreaHeight = Math.max(0, availableHeight - nonBodyHeight)
                        fitRows = Math.floor(rowsAreaHeight / rowHeight)
                    }

                    fitRows = Math.max(1, fitRows)
                    fitCallback(fitRows)
                    didComputeFit = true

                    // Gentle safeguard: if Fit computes to 1, it may be a transient layout/viewport
                    // measurement issue (especially in app-shell / PWA contexts). Retry once shortly
                    // after to self-correct without changing the algorithm.
                    if (fitRows === 1 && fitRetryTimeoutId == null) {
                        fitNeedsRecalcRef.current = true
                        fitRetryTimeoutId = window.setTimeout(() => {
                            fitRetryTimeoutId = null
                            scheduleSync(true)
                        }, 250)
                    }
                }
                if (didComputeFit) {
                    if (fitRetryTimeoutId == null) {
                        fitNeedsRecalcRef.current = false
                    }
                } else if (tableEl && theadEl && rows.length === 0) {
                    fitNeedsRecalcRef.current = false
                }
            }
        }

        function handleTopScroll() {
            if (syncLockRef.current) {
                return
            }
            syncLockRef.current = true
            bottomEl.scrollLeft = topEl.scrollLeft
            syncLockRef.current = false
        }

        function handleBottomScroll() {
            if (syncLockRef.current) {
                return
            }
            syncLockRef.current = true
            topEl.scrollLeft = bottomEl.scrollLeft
            syncLockRef.current = false
        }

        let rafId = /** @type {number | null} */ (null)
        let settleTimeoutId = /** @type {number | null} */ (null)
        let fitRetryTimeoutId = /** @type {number | null} */ (null)
        let lastLiveUpdateAt = 0

        function scheduleSync(resetSettleTimer = false) {
            const now = performance.now()
            const shouldRunLiveUpdate = (
                now - lastLiveUpdateAt >= RESIZE_LIVE_UPDATE_INTERVAL_MS
            )

            if (shouldRunLiveUpdate && rafId == null) {
                rafId = window.requestAnimationFrame(() => {
                    rafId = null
                    lastLiveUpdateAt = performance.now()
                    syncScrollMetrics()
                })
            }

            if (resetSettleTimer) {
                if (settleTimeoutId != null) {
                    window.clearTimeout(settleTimeoutId)
                }
                settleTimeoutId = window.setTimeout(() => {
                    if (rafId != null) {
                        window.cancelAnimationFrame(rafId)
                        rafId = null
                    }
                    syncScrollMetrics()
                }, RESIZE_SETTLE_DELAY_MS)
            }
        }

        const observer = new ResizeObserver(() => scheduleSync(false))
        observer.observe(bottomEl)
        const tableEl = bottomEl.querySelector("table")
        if (tableEl) {
            observer.observe(tableEl)
        }

        const rootObserver = new ResizeObserver(() => scheduleSync(false))
        if (rootEl) {
            rootObserver.observe(rootEl)
        }

        topEl.addEventListener("scroll", handleTopScroll)
        bottomEl.addEventListener("scroll", handleBottomScroll)
        const onWindowResize = () => {
            fitNeedsRecalcRef.current = activePerPageSelection === "fit"
            scheduleSync(true)
        }
        window.addEventListener("resize", onWindowResize)

        syncScrollMetrics()

        return () => {
            observer.disconnect()
            rootObserver.disconnect()
            topEl.removeEventListener("scroll", handleTopScroll)
            bottomEl.removeEventListener("scroll", handleBottomScroll)
            window.removeEventListener("resize", onWindowResize)
            if (rafId != null) {
                window.cancelAnimationFrame(rafId)
            }
            if (settleTimeoutId != null) {
                window.clearTimeout(settleTimeoutId)
            }
            if (fitRetryTimeoutId != null) {
                window.clearTimeout(fitRetryTimeoutId)
            }
        }
    }, [headers, rows, isLoadingInitial])

    function renderLoadingBody() {
        const colSpan = Math.max(1, headers.length)
        return (
            <tr>
                <td colSpan={colSpan} className="p-0">
                    <div
                        className="d-flex flex-column align-items-center justify-content-center"
                        style={{ minHeight: `${loadingBodyMinHeightPx}px` }}
                    >
                        <div
                            className="spinner-border text-primary"
                            role="status"
                            style={{ width: "64px", height: "64px", borderWidth: "6px" }}
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-3 text-secondary fw-semibold">Loading...</div>
                    </div>
                </td>
            </tr>
        )
    }

    const refetchLoaderBottomPx = (
        (showPagination ? footerBlockHeightPx : 0) +
        footerFillerBlockHeightPx +
        REFETCH_LOADER_ABOVE_FOOTER_GAP_PX
    )

    return (
        <div
            ref={rootRef}
            className={
                displayClass +
                " v2-table-scroll-root position-relative bg-white rounded overflow-hidden " +
                (stretchFooterToBottom ? "h-100 d-flex flex-column flex-grow-1" : "")
            }
        >
            <div
                ref={topScrollRef}
                className="table-responsive border-bottom"
                style={{
                    height: `${TOP_SCROLLBAR_HEIGHT_PX}px`,
                    overflowY: "hidden",
                    display: (hasHorizontalOverflow || DEBUG_FORCE_HORIZONTAL_SCROLLBAR) ? "block" : "none",
                }}
            >
                <div ref={topScrollInnerRef} style={{ height: "1px" }} />
            </div>
            <div
                ref={bottomScrollRef}
                className={
                    "table-responsive " +
                    (stretchFooterToBottom ?
                        (stretchInitialLoadLayout ? "flex-grow-1 min-h-0 " : "flex-grow-0 ") :
                        "")
                }
                style={{
                    overflowX: (hasHorizontalOverflow || DEBUG_FORCE_HORIZONTAL_SCROLLBAR) ? "scroll" : "auto",
                    ...(isLoadingInitial && stretchFooterToBottom ? { overflowY: "hidden" } : {}),
                }}
            >
                <table
                    className="table table-hover mb-0 v2-table-fixed"
                    style={{ width: tableWidthPx > 0 ? `${tableWidthPx}px` : undefined }}
                >
                    <colgroup>
                        {headers.map((header, index) => (
                            <col
                                key={header.key}
                                style={{
                                    width: columnWidths[index] ?
                                        `${columnWidths[index]}px` :
                                        undefined,
                                }}
                            />
                        ))}
                    </colgroup>
                    <thead className="bg-light">
                        <tr>
                            {headers.map((header) => (
                                <th
                                    key={header.key}
                                    scope="col"
                                    className={header.className ?? ""}
                                    style={{
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        ...header.style,
                                    }}
                                >
                                    {renderHeaderContent(header)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {isLoadingInitial ? renderLoadingBody() : (
                            rows.map((row, index) => (
                                renderRow(row, index, { getCellProps, getRowProps })
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {showPagination ? (
                <div
                    ref={footerRef}
                    className={
                        "d-flex align-items-center justify-content-end gap-3 px-3 " +
                        "py-2 border-top bg-body-tertiary " +
                        (stretchFooterToBottom ? "flex-shrink-0" : "")
                    }
                    style={stretchFooterToBottom ? { alignItems: "flex-start" } : undefined}
                >
                    <div className="d-flex align-items-center gap-2 text-secondary">
                        <span style={{ fontSize: "0.875rem" }}>Rows per page:</span>
                        <DropdownButton
                            title={activePerPageSelection === "fit" ? "Fit" : String(perPage)}
                            variant="light"
                            size="sm"
                            className="border shadow-sm"
                        >
                            {showFitOption ? (
                                <Dropdown.Item
                                    active={activePerPageSelection === "fit"}
                                    onClick={() => onPerPageChange?.("fit")}
                                >
                                    Fit
                                </Dropdown.Item>
                            ) : null}
                            {rowsPerPageOptions.map((size) => (
                                <Dropdown.Item
                                    key={size}
                                    active={activePerPageSelection === size}
                                    onClick={() => onPerPageChange?.(size)}
                                >
                                    {size}
                                </Dropdown.Item>
                            ))}
                        </DropdownButton>
                    </div>

                    <span className="text-secondary" style={{ fontSize: "0.875rem" }}>
                        {rangeStart}-{rangeEnd} of {totalRows}
                    </span>

                    <div className="d-inline-flex align-items-center gap-1">
                        <button
                            type="button"
                            className="btn btn-sm btn-light border d-inline-flex align-items-center justify-content-center"
                            onClick={() => onPageChange?.(1)}
                            disabled={!canGoPrev}
                            aria-label="First page"
                        >
                            <ChevronsLeft size={16} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-light border d-inline-flex align-items-center justify-content-center"
                            onClick={() => onPageChange?.(page - 1)}
                            disabled={!canGoPrev}
                            aria-label="Previous page"
                        >
                            <ChevronLeft size={16} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-light border d-inline-flex align-items-center justify-content-center"
                            onClick={() => onPageChange?.(page + 1)}
                            disabled={!canGoNext}
                            aria-label="Next page"
                        >
                            <ChevronRight size={16} aria-hidden />
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-light border d-inline-flex align-items-center justify-content-center"
                            onClick={() => onPageChange?.(totalPages)}
                            disabled={!canGoNext}
                            aria-label="Last page"
                        >
                            <ChevronsRight size={16} aria-hidden />
                        </button>
                    </div>
                </div>
            ) : null}
            {showPagination && stretchFooterToBottom && !isLoadingInitial ? (
                <div ref={footerFillerRef} className="bg-body-tertiary flex-grow-1" />
            ) : null}
            <V2TableRefetchLoader
                show={Boolean(showRefetchLoader)}
                bottomPx={refetchLoaderBottomPx}
            />
        </div>
    )
}
